import { Box, Stack, Table, TableBody, TableContainer, Typography } from "@mui/material";
import { useSettings } from "@sinch/core/providers/AppSettings";
import React, { FC, Fragment, ReactNode, useState } from "react";
import { usePosition } from "../../Position/usePosition";
import { ShiftPanelDetailFragment } from "../queries.types";
import { AttendanceSorter, AttendanceSortField, SortableAttendance, sortAttendances } from "./attendanceSort";
import { AttendanceSortMenu } from "./AttendanceSortMenu";
import { PositionHead } from "./PositionHead";

interface ShiftAttendanceTableProps {
  shift: ShiftPanelDetailFragment;
  /**
   * `sortRows` applies the sorting picked in the sort menu, use it on the rendered rows
   */
  renderRows: (position: ShiftPanelDetailFragment["positions"][0], sortRows: AttendanceSorter) => React.ReactNode;
  label: ReactNode;
  labelIcon?: ReactNode;
  /**
   * If true, capacities of position will be added to end of row
   */
  includeCapacity?: boolean;
}
export const ShiftAttendanceTable: FC<ShiftAttendanceTableProps> = ({
  shift,
  renderRows,
  label,
  labelIcon,
  includeCapacity,
}) => {
  const { sortPositions } = usePosition();
  const { locale } = useSettings();
  const [sortField, setSortField] = useState<AttendanceSortField | null>(AttendanceSortField.Surname);
  const sortedPositions = sortPositions(shift.positions);
  const sortRows = <T extends SortableAttendance>(rows: readonly T[]): T[] =>
    sortAttendances(rows, sortField, locale);
  return (
    <TableContainer>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
          ml: 2,
        }}
      >
        <Stack alignItems="center" direction="row" gap={1}>
          {labelIcon}
          <Typography variant="descriptionSemi">{label}</Typography>
        </Stack>
        <AttendanceSortMenu onChange={setSortField} value={sortField} />
      </Box>
      <Table padding="none">
        <TableBody>
          {sortedPositions.map((position) => (
            <Fragment key={position.id}>
              <PositionHead includeCapacity={includeCapacity} position={position} />
              {renderRows(position, sortRows)}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
